'use client';

import { SvgChevronleft, SvgChevronright, SvgVisible } from '@common/icon';
import { cn } from '@core/utils';
import dayjs from 'dayjs';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState } from 'react';

import { CustomLink } from '@/app/_components/CustomLink';
import { Image } from '@/components/client';
import { DONATION_ROUTES, WELLO_IMAGE_PREFIX_URL } from '@/constants';

export interface Product {
  productNumber: string;
  image: string;
  region: string;
  name: string;
  views: number;
}

interface MonthlyPolicyClientProps {
  products: Product[];
}

const chunkArray = <T,>(arr: T[], size: number) =>
  Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
    arr.slice(i * size, (i + 1) * size),
  );

const TEN_THOUSAND = 10000;

const formatViewCount = (count: number) => {
  const safeCount = Math.max(0, count);

  if (safeCount < TEN_THOUSAND) {
    return `${safeCount.toLocaleString()}`;
  }

  const t = safeCount / TEN_THOUSAND;

  const roundedTenThousand = Math.floor(t * 10) / 10;
  const integerTenThousand = Math.floor(roundedTenThousand);
  const decimalPart = Math.floor((roundedTenThousand % 1) * 10);

  if (decimalPart >= 9) {
    return `${(integerTenThousand + 1).toLocaleString()}만`;
  }

  return `${integerTenThousand.toLocaleString()}만`;
};

export const MonthlyPolicyClient = ({ products }: MonthlyPolicyClientProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'start',
  });

  const groupedProducts = chunkArray(products, 3);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  const scrollTo = (index: number) => {
    emblaApi?.scrollTo(index);
  };

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  return (
    <section>
      <div className="flex items-center justify-between px-28 pb-16">
        <div>
          <h2 className="font-700 text-Gray-900 typo-body2">
            {dayjs().year() === 2025
              ? "연말 추천정책 '고향사랑기부제'"
              : "이 달의 정책 '고향사랑기부제'"}
          </h2>
          <p className="font-500 text-Gray-600 typo-body4">
            세액공제 혜택+3만원 특산품 챙기세요
          </p>
        </div>
        <div className="items-center gap-4">
          <div className="hidden gap-4 mobile-sm:flex">
            {groupedProducts.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => scrollTo(index)}
                className={cn(
                  'size-6 rounded-pill transition-colors',
                  index === selectedIndex ? 'bg-Gray-500' : 'bg-Gray-300',
                )}
                aria-label={`${index + 1}번째 슬라이드로 이동`}
              />
            ))}
          </div>
          <div className="flex items-center gap-4 mobile-sm:hidden">
            <button
              type="button"
              onClick={scrollPrev}
              className="flex size-24 items-center justify-center rounded-small border border-Gray-200 bg-Gray-white text-Gray-600 transition-colors hover:bg-Gray-25"
              aria-label="이전 슬라이드"
            >
              <SvgChevronleft className="size-16" />
            </button>
            <button
              type="button"
              onClick={scrollNext}
              className="flex size-24 items-center justify-center rounded-small border border-Gray-200 bg-Gray-white text-Gray-600 transition-colors hover:bg-Gray-25"
              aria-label="다음 슬라이드"
            >
              <SvgChevronright className="size-16" />
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {groupedProducts.map((group, groupIndex) => (
            <div key={groupIndex} className="min-w-0 flex-[0_0_100%] px-20">
              <div className="flex gap-8 rounded-xxlarge bg-Gray-white p-20">
                {group.map((product, productIndex) => (
                  <CustomLink
                    key={product.productNumber || `product-${groupIndex}-${productIndex}`}
                    href={DONATION_ROUTES.GIFTS_DETAIL.pathname({
                      productNumber: product.productNumber,
                    })}
                    className="group -m-8 block flex-1 rounded-small p-8 transition-transform duration-150 active:scale-[0.98]"
                  >
                    <div className="relative mb-8">
                      <div className="relative aspect-square w-full overflow-hidden rounded-small bg-Gray-200">
                        {product.image && (
                          <Image
                            src={product.image}
                            alt={product.name}
                            fill
                            sizes="(max-width: 768px) 30vw, 15vw"
                            className="object-cover transition-transform duration-200 ease-out group-hover:scale-110"
                          />
                        )}
                      </div>
                      <div className="absolute left-0 top-0 flex h-18 w-34 items-center">
                        <Image
                          alt="pick"
                          src={`${WELLO_IMAGE_PREFIX_URL}/static/donation/main/pick.png`}
                          width={34 * 2}
                          height={18 * 2}
                          sizes="34px"
                          quality={90}
                          className="min-h-18"
                          loading="lazy"
                        />
                      </div>
                    </div>
                    <p className="mb-2 font-600 text-Gray-600 typo-caption1">
                      {product.region}
                    </p>
                    <h3 className="mb-8 line-clamp-2 min-h-32 font-600 !leading-[130%] text-Gray-900 transition-colors duration-150 typo-caption1 group-hover:text-Gray-700">
                      {product.name}
                    </h3>
                    <div className="flex items-center gap-4">
                      <SvgVisible className="size-16 text-B-500" />
                      <span className="font-500 text-B-500 typo-caption1">
                        {formatViewCount(product.views)}명 조회
                      </span>
                    </div>
                  </CustomLink>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
